import { z } from 'zod';

import {
  PlannerActionIntent,
  PlannerActivityType,
} from '../types/planner';

/**
 * Form schemas for the scheduling flows (Wave 8 Agent A).
 *
 * The shapes adhere to MIGRATION_PLAYBOOK §3.5: every field is required, empty
 * strings are used for "no selection", and literal-unions stand in for numeric
 * enums so `z.input` and `z.output` stay identical and `zodResolver` satisfies
 * the react-hook-form `Resolver` constraint.
 */

/* -------------------------------------------------------------------------- */
/* Enum literal unions                                                         */
/* -------------------------------------------------------------------------- */

export const plannerActionIntentSchema = z.union([
  z.literal(PlannerActionIntent.None),
  z.literal(PlannerActionIntent.Erect),
  z.literal(PlannerActionIntent.Modify),
  z.literal(PlannerActionIntent.Dismantle),
  z.literal(PlannerActionIntent.Mixed),
  z.literal(PlannerActionIntent.Inspection),
  z.literal(PlannerActionIntent.Maintenance),
]);
export type PlannerActionIntentSchemaValue = z.infer<
  typeof plannerActionIntentSchema
>;

export const plannerActivityTypeSchema = z.union([
  z.literal(PlannerActivityType.Job),
  z.literal(PlannerActivityType.Training),
  z.literal(PlannerActivityType.Meeting),
  z.literal(PlannerActivityType.Other),
]);
export type PlannerActivityTypeSchemaValue = z.infer<
  typeof plannerActivityTypeSchema
>;

/* -------------------------------------------------------------------------- */
/* Drag-and-drop schedule payload                                              */
/* -------------------------------------------------------------------------- */

/**
 * Used when an unplanned task card is dropped onto a (crew, day) cell. The
 * supervisor is the current user by convention; the dialog can override.
 */
export const scheduleUnplannedTaskSchema = z
  .object({
    workOrderTaskId: z.string().min(1, 'Task is required'),
    supervisorUserId: z.string().min(1, 'Supervisor is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
    notes: z.string().max(2000).default(''),
    crewUserId: z.string().default(''),
    assetId: z.string().default(''),
  })
  .refine(
    (data) => data.endTime > data.startTime,
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    },
  );

export type ScheduleUnplannedTaskValues = z.infer<
  typeof scheduleUnplannedTaskSchema
>;

export const defaultScheduleUnplannedTaskValues: ScheduleUnplannedTaskValues = {
  workOrderTaskId: '',
  supervisorUserId: '',
  startTime: '',
  endTime: '',
  notes: '',
  crewUserId: '',
  assetId: '',
};

/* -------------------------------------------------------------------------- */
/* Execution review (approve / reject pending activities)                      */
/* -------------------------------------------------------------------------- */

/**
 * Schema for the supervisor's reject reason. Approve doesn't take a reason but
 * the dialog reuses this form shape for symmetry.
 */
export const executionReviewSchema = z
  .object({
    /** Required when target status is Rejected. */
    reason: z.string().max(1000).default(''),
  })
  .refine(
    () => true, // Object-level refine kept for future cross-field rules.
    { message: '' },
  );

export type ExecutionReviewValues = z.infer<typeof executionReviewSchema>;

export const defaultExecutionReviewValues: ExecutionReviewValues = {
  reason: '',
};

/* -------------------------------------------------------------------------- */
/* Execution capture (recording what happened on site)                         */
/* -------------------------------------------------------------------------- */

export const executionResultSchema = z.union([
  z.literal('Completed'),
  z.literal('Partial'),
  z.literal('NotDone'),
]);
export type ExecutionResultValue = z.infer<typeof executionResultSchema>;

/**
 * One row in the execution-capture form — mirrors `ExecutionCaptureResult` on
 * the API side but stored with empty-string defaults to satisfy RHF.
 */
export const executionLineCaptureSchema = z
  .object({
    executionLineId: z.string().min(1),
    result: executionResultSchema,
    /** ISO date — required when `result === 'Partial' | 'NotDone'`. */
    continueOn: z.string().default(''),
  })
  .refine(
    (data) => data.result === 'Completed' || data.continueOn.length > 0,
    {
      message: 'Continue-on date is required for partial / not-done lines',
      path: ['continueOn'],
    },
  );

export type ExecutionLineCaptureValues = z.infer<
  typeof executionLineCaptureSchema
>;

export const executionCaptureSchema = z.object({
  activityId: z.string().min(1),
  lines: z.array(executionLineCaptureSchema).min(1, 'Capture at least one line'),
});

export type ExecutionCaptureValues = z.infer<typeof executionCaptureSchema>;

export const defaultExecutionCaptureValues: ExecutionCaptureValues = {
  activityId: '',
  lines: [],
};
